// The lines the column draws, as data — one colour per thing, everywhere.
//
// Cody, 2026-10-04: "In Les's original graphs it's perfectly clear: one line,
// one colour." So every line on every track has exactly one palette slot, and
// the same slot on every chart it appears on: the MEASUREMENT is always ink,
// Open-Meteo is always violet, HYCOM amber, CMEMS green, our forecast the
// console accent, PG&E's offset model grey. A reader who learns a colour on
// the first track can use it on the last. No fills, no bands.
//
// Colours are RESOLVED here from the live theme (a canvas cannot read var();
// passing the variable name drew every line black once already) and the
// legends are readout rows — swatch, name, value — rendered by
// chart/readout.ts above each plot, so colour is never the only label.

import type { ReadoutModel, ReadoutRow } from "../../chart/readout.js";
import type { Series } from "../../chart/chart.js";
import type { TempPoint } from "./tempchart.js";
import type { StationModels } from "./payload.js";

export const LINE_SLOT = {
  measured: "--ink",
  openmeteo: "--m4",
  hycom: "--m3",
  cmems: "--m2",
  ours: "--accent",
  pge: "--muted",
} as const;
export type LineKey = keyof typeof LINE_SLOT;

const FALLBACK: Record<LineKey, string> = {
  measured: "#16232e", openmeteo: "#7b5ea7", hycom: "#d98a2b", cmems: "#2f9e78",
  ours: "#0f6b74", pge: "#6b7280",
};

export const LINE_LABEL: Record<LineKey, string> = {
  measured: "Measured",
  openmeteo: "Open-Meteo",
  hycom: "HYCOM",
  cmems: "CMEMS",
  ours: "SeaState",
  pge: "Open-Meteo + offset",
};

/** The models a station chart may carry, in the order they are drawn and listed. */
export const MODEL_ORDER = ["openmeteo", "hycom", "cmems"] as const;

export type Colours = Record<LineKey, string> & {
  /** The range's swatch: our colour at the tint the chart fills it with. Set
   *  by tracks.ts after resolving; falls back to the line colour. */
  range?: string;
};

/** Resolve every slot once. `cssVar` reads the live theme; tracks.ts calls this
 *  again when the theme changes. */
export function resolveColours(cssVar: (slot: string, fallback: string) => string): Colours {
  const out = {} as Colours;
  for (const k of Object.keys(LINE_SLOT) as LineKey[]) out[k] = cssVar(LINE_SLOT[k], FALLBACK[k]);
  return out;
}

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-04" → "Sun 4 Oct". Zone-free: a calendar date has no time to shift. */
export function dayTitle(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m === null) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return iso;
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

function f1(v: number | null | undefined): string {
  return typeof v === "number" && Number.isFinite(v) ? `${v.toFixed(1)} °F` : "—";
}

/** Align [date, value] pairs to the shared dates; null where a day has none. */
export function alignPairs(
  pairs: readonly (readonly [string, number])[] | undefined,
  dates: readonly string[],
): (number | null)[] {
  const by = new Map<string, number>();
  for (const [d, v] of pairs ?? []) if (typeof v === "number" && Number.isFinite(v)) by.set(d, v);
  return dates.map((d) => by.get(d) ?? null);
}

/** A station track's series: the station's own readings, heavy, then each model
 *  that stands in for it, thin — only the ones with a value in the window. */
export function stationSeries(
  models: StationModels | undefined,
  dates: readonly string[],
  col: Colours,
): Series[] {
  const out: Series[] = [];
  const measured = alignPairs(models?.measured, dates);
  if (measured.some((v) => v !== null)) {
    out.push({ label: LINE_LABEL.measured, data: measured, color: col.measured, width: 2.4, order: 10 });
  }
  for (const k of MODEL_ORDER) {
    const data = alignPairs(models?.[k], dates);
    if (!data.some((v) => v !== null)) continue;
    out.push({ label: LINE_LABEL[k], data, color: col[k], width: 1.6, order: 1 });
  }
  return out;
}

/** Our line on the intake chart is one line from three sources: the hindcast
 *  (fitted before the window, run on observed inputs), the estimate filling
 *  the reporting gap, and the forecast. One colour, because it is one model. */
export function oursOf(pt: TempPoint | null | undefined): number | null {
  const v = pt?.hindcast ?? pt?.estimate ?? pt?.forecast;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** What a day's numbers ARE, said in the strip's title so the label travels
 *  with the value: a past day is scored on the archived analysis, the gap days
 *  are our estimate, the forward days are forecasts. */
export function basisOf(pt: TempPoint | null | undefined): string {
  if (pt == null) return "";
  if (typeof pt.forecast === "number") return "forecast";
  if (typeof pt.hindcast === "number") return "on the analysis";
  if (typeof pt.estimate === "number") return "estimate";
  return "";
}

/** The intake chart's legend rows: measured, ours, PG&E's method — and on a
 *  forward day, the range our prediction sits in. A row exists only for a
 *  line that is drawn on that day, so the past has three rows and the
 *  forecast four. */
export function headToHeadRows(pt: TempPoint | null, col: Colours): ReadoutRow[] {
  const partial =
    pt?.measured == null && typeof pt?.measuredPartial === "number"
      ? `${f1(pt.measuredPartial)} to ${pt.measuredTo ?? "?"}${typeof pt.measuredHours === "number" ? ` (${pt.measuredHours} h)` : ""}`
      : null;
  const rows: ReadoutRow[] = [
    { label: LINE_LABEL.measured, color: col.measured, value: partial ?? f1(pt?.measured) },
    { label: LINE_LABEL.ours, color: col.ours, value: f1(oursOf(pt)) },
    { label: LINE_LABEL.pge, color: col.pge, value: f1(pt?.pge) },
  ];
  const lo = pt?.rangeLo;
  const hi = pt?.rangeHi;
  if (typeof lo === "number" && typeof hi === "number") {
    const n = pt?.members ?? 0;
    rows.push({
      label: n > 0 ? `${n}-model range` : "Model range",
      color: col.range ?? col.ours,
      value: `${lo.toFixed(1)}–${hi.toFixed(1)} °F`,
    });
  }
  return rows;
}

export function headToHeadReadout(
  pt: TempPoint | null,
  index: number | null,
  live: boolean,
  col: Colours,
): ReadoutModel {
  const basis = basisOf(pt);
  return {
    title: pt === null ? "" : `${dayTitle(pt.date)}${basis ? ` · ${basis}` : ""}`,
    rows: pt === null ? [] : headToHeadRows(pt, col),
    extras: [],
    index,
    live,
  };
}

/** Index of the last non-null value, for a meter that should show the latest
 *  measurement when nothing is being pointed at. */
export function lastIndex(data: readonly (number | null)[]): number | null {
  for (let i = data.length - 1; i >= 0; i--) if (data[i] !== null) return i;
  return null;
}
